import { useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

/**
 * Hook que monitorea la versión del caché de contenido en el backend
 * Cuando la versión cambia, limpia el caché local del cliente
 * 
 * Se ejecuta cada 30 segundos para verificar si hay cambios en el servidor
 */
export default function useContentCacheInvalidator() {
  const lastVersionRef = useRef(null);
  const checkIntervalRef = useRef(null);

  useEffect(() => {
    /**
     * Función para verificar la versión del caché
     */
    const checkCacheVersion = async () => {
      try {
        const response = await axios.get(`${API}/content/cache-version`);
        const { content_version, promotions_version } = response.data;

        // Verificar si la versión de contenido ha cambiado
        if (
          lastVersionRef.current &&
          lastVersionRef.current.content_version !== content_version
        ) {
          console.log(
            '🔄 Caché de contenido invalidado en el servidor. Limpiando caché local...'
          );
          invalidateContentCache();
        }

        // Verificar si la versión de promociones ha cambiado
        if (
          lastVersionRef.current &&
          lastVersionRef.current.promotions_version !== promotions_version
        ) {
          console.log(
            '🔄 Caché de promociones invalidado en el servidor. Limpiando caché local...'
          );
          invalidatePromotionsCache();
        }

        // Guardar versiones actuales
        lastVersionRef.current = {
          content_version,
          promotions_version,
        };
      } catch (error) {
        // Silently fail si hay error de red
        console.debug('Error checking cache version:', error.message);
      }
    };

    /**
     * Limpiar localStorage de contenido y sesión storage
     */
    const invalidateContentCache = () => {
      // Limpiar las claves de caché de contenido del ContentCacheContext
      localStorage.removeItem('showlib_content');
      localStorage.removeItem('showlib_content_time');
      
      // Limpiar caché comprimido por tipo
      localStorage.removeItem('showlib_movies');
      localStorage.removeItem('showlib_series');
      localStorage.removeItem('showlib_novelas');
      
      // Limpiar del sessionStorage también
      sessionStorage.removeItem('showlib_content');
      sessionStorage.removeItem('showlib_content_time');
      sessionStorage.removeItem('showlib_movies');
      sessionStorage.removeItem('showlib_series');
      sessionStorage.removeItem('showlib_novelas');
      
      // Limpiar caché de cacheManager (si existe)
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('app_cache_')) {
          localStorage.removeItem(key);
        }
      });
      
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('app_cache_')) {
          sessionStorage.removeItem(key);
        }
      });

      // Emitir evento para que otros componentes sepan que el caché fue invalidado
      window.dispatchEvent(
        new CustomEvent('content-cache-invalidated', {
          detail: { timestamp: Date.now() },
        })
      );
    };

    /**
     * Limpiar localStorage de promociones
     */
    const invalidatePromotionsCache = () => {
      // Limpiar claves relacionadas a promociones
      Object.keys(localStorage).forEach(key => {
        if (key.includes('promotion') || key.includes('promo')) {
          localStorage.removeItem(key);
        }
      });

      Object.keys(sessionStorage).forEach(key => {
        if (key.includes('promotion') || key.includes('promo')) {
          sessionStorage.removeItem(key);
        }
      });

      // Emitir evento
      window.dispatchEvent(
        new CustomEvent('promotions-cache-invalidated', {
          detail: { timestamp: Date.now() },
        })
      );
    };

    // Realizar la primera verificación
    checkCacheVersion();

    // Configurar intervalo para verificar cada 30 segundos
    checkIntervalRef.current = setInterval(() => {
      checkCacheVersion();
    }, 30 * 1000);

    // Limpiar intervalo al desmontar
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);
}
